/**
 * Products Section Component
 * Smart container for Products/SKUs admin management
 */

import { useEffect, useCallback, useState, lazy, Suspense } from 'react'
import { useDispatch, useSelector, useTranslation } from '@spcc/core'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  Package,
  Layers,
  Tag,
  Plus,
  RefreshCw,
  Search,
  MoreHorizontal,
  LayoutGrid,
  LayoutList,
  type LucideIcon,
} from '@spcc/uikit'

import { productsActions } from '../../store/products.slice'
import {
  selectSortedSkus,
  selectFilteredSkus,
  selectFilteredVariants,
  selectActiveCategories,
  selectSortedCategories,
  selectProductsStats,
  selectProductsFilter,
  selectProductsLoading,
  selectProductsSaving,
  selectPortalAvailabilityUpdatingById,
  selectProductsError,
  selectEditingSku,
  selectEditingVariant,
  selectEditingCategory,
} from '../../store/products.selectors'
import type {
  SKU,
  Variant,
  Category,
  VariantPortalAvailability,
  CreateSKURequest,
  UpdateSKURequest,
  CreateVariantRequest,
  UpdateVariantRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from '../../domain/types'
import { filterVariantsBySkuId, getLocalizedName, formatWeight } from '../../domain/utils'

import { SKUCard } from '../cards/sku-card'
import { VariantCard } from '../cards/variant-card'
const SKUFormDrawer = lazy(() => import('../drawers/sku-form-drawer').then(m => ({ default: m.SKUFormDrawer })))
const VariantFormDrawer = lazy(() => import('../drawers/variant-form-drawer').then(m => ({ default: m.VariantFormDrawer })))
const SKUDetailDrawer = lazy(() => import('../drawers/sku-detail-drawer').then(m => ({ default: m.SKUDetailDrawer })))
const DeleteProductDialog = lazy(() => import('../modals/delete-product-dialog').then(m => ({ default: m.DeleteProductDialog })))
const CategoryFormDrawer = lazy(() => import('../drawers/category-form-drawer').then(m => ({ default: m.CategoryFormDrawer })))

type ImageFilePicker = () => Promise<File[]>

interface ProductsSectionProps {
  pickImageFiles?: ImageFilePicker
}

// ============================================================================
// Stats Card Component
// ============================================================================

function StatsCard({
  title,
  value,
  icon: Icon,
  variant = 'default',
}: {
  title: string
  value: number | string
  icon: LucideIcon
  variant?: 'default' | 'success' | 'warning'
}) {
  return (
    <Card className="min-w-0">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2 px-3 pt-3 sm:px-6 sm:pt-6">
        <CardTitle className="text-xs font-medium text-muted-foreground sm:text-sm">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent className="px-3 pb-3 sm:px-6 sm:pb-6">
        <div
          className={cn(
            'break-words text-xl font-bold tabular-nums sm:text-2xl',
            variant === 'success' && 'text-green-600',
            variant === 'warning' && 'text-amber-600'
          )}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================================================
// Loading Skeleton
// ============================================================================

function ProductsSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Skeleton key={i} className="h-40 w-full" />
      ))}
    </div>
  )
}

const ALL_SKUS_FILTER_VALUE = 'all'

function toSkuDocumentIdFilter(value: string): string | undefined {
  return value === ALL_SKUS_FILTER_VALUE ? undefined : value
}

function applyVariantTabFilters(variants: Variant[], skuFilterValue: string): Variant[] {
  return filterVariantsBySkuId(variants, toSkuDocumentIdFilter(skuFilterValue))
}

// ============================================================================
// Main Component
// ============================================================================

export function ProductsSection({ pickImageFiles }: ProductsSectionProps = {}) {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()

  // Selectors
  const allSkus = useSelector(selectSortedSkus)
  const skus = useSelector(selectFilteredSkus)
  const variants = useSelector(selectFilteredVariants)
  const categories = useSelector(selectActiveCategories)
  const allCategories = useSelector(selectSortedCategories)
  const stats = useSelector(selectProductsStats)
  const filter = useSelector(selectProductsFilter)
  const isLoading = useSelector(selectProductsLoading)
  const isSaving = useSelector(selectProductsSaving)
  const portalAvailabilityUpdatingById = useSelector(selectPortalAvailabilityUpdatingById)
  const error = useSelector(selectProductsError)
  const editingSku = useSelector(selectEditingSku)
  const editingVariant = useSelector(selectEditingVariant)
  const editingCategory = useSelector(selectEditingCategory)

  // Local state for drawers and dialogs
  const [activeTab, setActiveTab] = useState<'categories' | 'skus' | 'variants'>('categories')
  const [isSkuFormOpen, setIsSkuFormOpen] = useState(false)
  const [isVariantFormOpen, setIsVariantFormOpen] = useState(false)
  const [isSkuDetailOpen, setIsSkuDetailOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isCategoryFormOpen, setIsCategoryFormOpen] = useState(false)
  const [selectedSku, setSelectedSku] = useState<SKU | null>(null)
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  const [deleteItemType, setDeleteItemType] = useState<'sku' | 'variant'>('sku')
  const [variantSkuFilter, setVariantSkuFilter] = useState<string>(ALL_SKUS_FILTER_VALUE)
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table')
  const locale: 'en' | 'ar' = i18n.language === 'ar' ? 'ar' : 'en'

  // Fetch data on mount
  useEffect(() => {
    dispatch(productsActions.fetchProducts())
  }, [dispatch])

  // Handlers
  const handleRefresh = useCallback(() => {
    dispatch(productsActions.fetchProducts())
  }, [dispatch])

  const handleSearchChange = useCallback(
    (value: string) => {
      dispatch(productsActions.setFilter({ search: value }))
    },
    [dispatch]
  )

  const handleStatusChange = useCallback(
    (value: 'all' | 'active' | 'inactive') => {
      dispatch(productsActions.setFilter({ status: value }))
    },
    [dispatch]
  )

  const handlePackagingTypeChange = useCallback(
    (value: 'all' | 'bags' | 'bulk') => {
      dispatch(productsActions.setFilter({ packagingType: value }))
    },
    [dispatch]
  )

  const handleVariantSkuFilterChange = useCallback((value: string) => {
    setVariantSkuFilter(value)
  }, [])

  // SKU handlers
  const handleAddSku = useCallback(() => {
    setSelectedSku(null)
    setIsSkuFormOpen(true)
  }, [])

  const handleEditSku = useCallback((sku: SKU) => {
    setSelectedSku(sku)
    setIsSkuFormOpen(true)
    setIsSkuDetailOpen(false)
    dispatch(productsActions.fetchSkuForEdit(sku.documentId))
  }, [dispatch])

  const handleSkuSelect = useCallback((sku: SKU) => {
    setSelectedSku(sku)
    setIsSkuDetailOpen(true)
  }, [])

  const handleSkuFormSubmit = useCallback(
    (data: CreateSKURequest | UpdateSKURequest) => {
      if ('documentId' in data) {
        dispatch(productsActions.updateSku(data as UpdateSKURequest))
      } else {
        dispatch(productsActions.createSku(data as CreateSKURequest))
      }
      setIsSkuFormOpen(false)
    },
    [dispatch]
  )

  const handleDeleteSku = useCallback((sku: SKU) => {
    setSelectedSku(sku)
    setDeleteItemType('sku')
    setIsDeleteDialogOpen(true)
    setIsSkuDetailOpen(false)
  }, [])

  // Variant handlers
  const handleAddVariant = useCallback((sku?: SKU) => {
    setSelectedVariant(null)
    setSelectedSku(sku || null)
    setIsVariantFormOpen(true)
    setIsSkuDetailOpen(false)
  }, [])

  const handleEditVariant = useCallback((variant: Variant) => {
    setSelectedVariant(variant)
    setIsVariantFormOpen(true)
    dispatch(productsActions.fetchVariantForEdit(variant.documentId))
  }, [dispatch])

  const handleVariantFormSubmit = useCallback(
    (data: CreateVariantRequest | UpdateVariantRequest) => {
      if ('documentId' in data) {
        dispatch(productsActions.updateVariant(data as UpdateVariantRequest))
      } else {
        dispatch(productsActions.createVariant(data as CreateVariantRequest))
      }
      setIsVariantFormOpen(false)
    },
    [dispatch]
  )

  const handleDeleteVariant = useCallback((variant: Variant) => {
    setSelectedVariant(variant)
    setDeleteItemType('variant')
    setIsDeleteDialogOpen(true)
  }, [])

  // Category handlers
  const handleAddCategory = useCallback(() => {
    setSelectedCategory(null)
    setIsCategoryFormOpen(true)
  }, [])

  const handleEditCategory = useCallback((category: Category) => {
    setSelectedCategory(category)
    setIsCategoryFormOpen(true)
    dispatch(productsActions.fetchCategoryForEdit(category.documentId))
  }, [dispatch])

  const handleCategoryFormSubmit = useCallback(
    (data: CreateCategoryRequest | UpdateCategoryRequest) => {
      if ('documentId' in data) {
        dispatch(productsActions.updateCategory(data as UpdateCategoryRequest))
      } else {
        dispatch(productsActions.createCategory(data as CreateCategoryRequest))
      }
      setIsCategoryFormOpen(false)
    },
    [dispatch]
  )

  const handlePortalAvailabilityChange = useCallback(    (variant: Variant, portalAvailability: VariantPortalAvailability) => {
      dispatch(
        productsActions.updateVariantPortalAvailability({
          documentId: variant.documentId,
          portalAvailability,
        })
      )
    },
    [dispatch]
  )

  // Delete confirmation
  const handleConfirmDelete = useCallback(() => {
    if (deleteItemType === 'sku' && selectedSku) {
      dispatch(productsActions.deleteSku(selectedSku.documentId))
    } else if (deleteItemType === 'variant' && selectedVariant) {
      dispatch(productsActions.deleteVariant(selectedVariant.documentId))
    }
    setIsDeleteDialogOpen(false)
  }, [dispatch, deleteItemType, selectedSku, selectedVariant])

  // Get variants for selected SKU (for detail drawer)
  const skuVariants = selectedSku
    ? variants.filter((v) => v.skuDocumentId === selectedSku.documentId)
    : []

  // SKU summaries for variant form dropdown
  const skuSummaries = skus.map((sku) => ({
    id: sku.id,
    documentId: sku.documentId,
    name: sku.name,
    isActive: sku.isActive,
  }))

  const variantsForDisplay = applyVariantTabFilters(variants, variantSkuFilter)

  // Sync fresh bilingual data from Redux into form state when edit fetch completes
  useEffect(() => {
    if (editingSku && isSkuFormOpen) setSelectedSku(editingSku)
  }, [editingSku, isSkuFormOpen])

  useEffect(() => {
    if (editingVariant && isVariantFormOpen) setSelectedVariant(editingVariant)
  }, [editingVariant, isVariantFormOpen])

  useEffect(() => {
    if (editingCategory && isCategoryFormOpen) setSelectedCategory(editingCategory)
  }, [editingCategory, isCategoryFormOpen])

  return (
    <div className="flex min-w-0 flex-col gap-4 sm:gap-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
        <StatsCard
          title={t('products.stats.totalSkus')}
          value={stats.totalSkus}
          icon={Package}
        />
        <StatsCard
          title={t('products.stats.activeSkus')}
          value={stats.activeSkus}
          icon={Package}
          variant="success"
        />
        <StatsCard
          title={t('products.stats.totalVariants')}
          value={stats.totalVariants}
          icon={Layers}
        />
        <StatsCard
          title={t('products.stats.totalCategories')}
          value={stats.totalCategories}
          icon={Tag}
        />
      </div>

      {/* Main Content Card */}
      <Card className="min-w-0 overflow-hidden">
        <CardHeader className="flex flex-col items-stretch gap-3 space-y-0 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-xl sm:text-2xl">{t('products.title')}</CardTitle>
          <div className="grid grid-cols-[auto_1fr] items-center gap-2 sm:flex sm:justify-end">
            <Button variant="outline" size="icon" className="h-11 w-11 sm:h-9 sm:w-9" onClick={handleRefresh} disabled={isLoading}>
              <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            </Button>
            {activeTab === 'categories' ? (
              <Button onClick={handleAddCategory} className="h-11 min-w-0 sm:h-9">
                <Plus className="h-4 w-4 me-2" />
                <span className="truncate">{t('products.addCategory')}</span>
              </Button>
            ) : activeTab === 'skus' ? (
              <Button onClick={handleAddSku} className="h-11 min-w-0 sm:h-9">
                <Plus className="h-4 w-4 me-2" />
                <span className="truncate">{t('products.addSku')}</span>
              </Button>
            ) : (
              <Button onClick={() => handleAddVariant()} className="h-11 min-w-0 sm:h-9">
                <Plus className="h-4 w-4 me-2" />
                <span className="truncate">{t('products.addVariant')}</span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="min-w-0">
          {/* Error display */}
          {error && (
            <div className="mb-4 p-4 bg-destructive/10 text-destructive rounded-md">{error}</div>
          )}

          {/* Toolbar */}
          <div className="mb-5 flex flex-col items-stretch gap-3 sm:mb-6 sm:flex-row sm:items-center sm:gap-4">
            {/* Search */}
            <div className="relative w-full flex-1 sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t('products.searchPlaceholder')}
                value={filter.search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="ps-9"
              />
            </div>

            {/* Status Filter */}
            <Select value={filter.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="h-11 w-full sm:h-9 sm:w-[140px]">
                <SelectValue placeholder={t('products.filter.all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('products.filter.all')}</SelectItem>
                <SelectItem value="active">{t('products.filter.active')}</SelectItem>
                <SelectItem value="inactive">{t('products.filter.inactive')}</SelectItem>
              </SelectContent>
            </Select>

            <div className="inline-flex w-fit items-center rounded-2xl border border-border/70 bg-muted/50 p-1">
              <Button
                size="icon"
                variant="ghost"
                className={cn(
                  'h-8 w-8 rounded-xl transition-colors',
                  viewMode === 'table'
                    ? 'bg-background text-primary shadow-sm hover:bg-background'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                onClick={() => setViewMode('table')}
                aria-label={t('common.tableView', { defaultValue: 'Table View' })}
              >
                <LayoutList className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className={cn(
                  'h-8 w-8 rounded-xl transition-colors',
                  viewMode === 'cards'
                    ? 'bg-background text-primary shadow-sm hover:bg-background'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                onClick={() => setViewMode('cards')}
                aria-label={t('common.cardView', { defaultValue: 'Card View' })}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'categories' | 'skus' | 'variants')}>
            <div className="mb-4 -mx-6 overflow-x-auto px-6">
              <TabsList className="w-max min-w-full justify-start">
                <TabsTrigger value="categories" className="shrink-0 gap-2">
                  <Tag className="h-4 w-4" />
                  {t('products.stats.totalCategories')} ({allCategories.length})
                </TabsTrigger>
                <TabsTrigger value="skus" className="shrink-0 gap-2">
                  <Package className="h-4 w-4" />
                  {t('products.stats.totalSkus')} ({skus.length})
                </TabsTrigger>
                <TabsTrigger value="variants" className="shrink-0 gap-2">
                  <Layers className="h-4 w-4" />
                  {t('products.stats.totalVariants')} ({variantsForDisplay.length})
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Categories Tab */}
            <TabsContent value="categories">
              {isLoading && allCategories.length === 0 ? (
                <ProductsSkeleton />
              ) : allCategories.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  {t('products.noCategories')}
                </div>
              ) : (
                <div className="glass-card max-w-full overflow-hidden rounded-2xl border border-border/50">
                  <div className="overflow-x-auto">
                    <Table className="min-w-[720px]">
                      <TableHeader>
                        <TableRow className="border-border/40 hover:bg-transparent bg-muted/50">
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.table.name')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.table.code')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.table.color')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.table.parent')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.table.status')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider text-end w-[120px]">{t('products.table.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allCategories.map((category) => (
                          <TableRow
                            key={category.documentId}
                            className="cursor-pointer border-border/50 hover:bg-secondary/30 transition-colors"
                            onClick={() => handleEditCategory(category)}
                          >
                            <TableCell className="px-5 py-4 font-medium">
                              <div className="flex items-center gap-2">
                                {category.color && (
                                  <span
                                    className="inline-block h-3 w-3 rounded-full shrink-0"
                                    style={{ backgroundColor: category.color }}
                                  />
                                )}
                                {getLocalizedName(category.name, locale)}
                              </div>
                            </TableCell>
                            <TableCell className="px-5 py-4 font-mono text-xs text-muted-foreground">
                              {category.code ?? '-'}
                            </TableCell>
                            <TableCell className="px-5 py-4">
                              {category.color ? (
                                <span className="font-mono text-xs">{category.color}</span>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="px-5 py-4 text-muted-foreground text-sm">
                              {category.parent ? getLocalizedName(category.parent.name, locale) : '-'}
                            </TableCell>
                            <TableCell className="px-5 py-4">
                              {category.isActive ? t('common.active') : t('common.inactive')}
                            </TableCell>
                            <TableCell className="px-5 py-4 text-end">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-9 w-9 rounded-xl hover:bg-muted"
                                    aria-label={t('common.actions')}
                                  >
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  onClick={(e) => e.stopPropagation()}
                                  onPointerDown={(e) => e.stopPropagation()}
                                >
                                  <DropdownMenuItem
                                    onSelect={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      handleEditCategory(category)
                                    }}
                                  >
                                    {t('common.edit')}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* SKUs Tab */}
            <TabsContent value="skus">
              {isLoading && skus.length === 0 ? (
                <ProductsSkeleton />
              ) : skus.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  {t('products.noSkus')}
                </div>
              ) : viewMode === 'cards' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {skus.map((sku) => (
                    <SKUCard
                      key={sku.documentId}
                      sku={sku}
                      onSelect={handleSkuSelect}
                      onEdit={handleEditSku}
                      onDelete={handleDeleteSku}
                      locale={locale}
                    />
                  ))}
                </div>
              ) : (
                <div className="glass-card max-w-full overflow-hidden rounded-2xl border border-border/50">
                  <div className="overflow-x-auto">
                    <Table className="min-w-[720px]">
                      <TableHeader>
                        <TableRow className="border-border/40 hover:bg-transparent bg-muted/50">
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.table.name')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.table.code')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.table.status')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.table.variants')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.table.categories')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider text-end w-[180px]">{t('products.table.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {skus.map((sku) => (
                          <TableRow
                            key={sku.documentId}
                            className="cursor-pointer border-border/50 hover:bg-secondary/30 transition-colors"
                            onClick={() => handleSkuSelect(sku)}
                          >
                            <TableCell className="px-5 py-4 font-medium">{getLocalizedName(sku.name, locale)}</TableCell>
                            <TableCell className="px-5 py-4 font-mono text-xs text-muted-foreground">
                              {sku.documentId}
                            </TableCell>
                            <TableCell className="px-5 py-4">{sku.isActive ? t('common.active') : t('common.inactive')}</TableCell>
                            <TableCell className="px-5 py-4">{sku.variantCount ?? sku.variants?.length ?? 0}</TableCell>
                            <TableCell className="px-5 py-4">{sku.categories?.length ?? 0}</TableCell>
                            <TableCell className="px-5 py-4 text-end">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-9 w-9 rounded-xl hover:bg-muted"
                                    aria-label={t('common.actions')}
                                  >
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  onClick={(e) => e.stopPropagation()}
                                  onPointerDown={(e) => e.stopPropagation()}
                                >
                                  <DropdownMenuItem
                                    onSelect={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      handleEditSku(sku)
                                    }}
                                  >
                                    {t('common.edit')}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      handleDeleteSku(sku)
                                    }}
                                  >
                                    {t('common.delete')}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Variants Tab */}
            <TabsContent value="variants">
              <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                <Select value={filter.packagingType ?? 'all'} onValueChange={handlePackagingTypeChange}>
                  <SelectTrigger className="h-11 w-full sm:h-9 sm:w-[180px]">
                    <SelectValue placeholder={t('products.filter.allPackaging')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('products.filter.allPackaging')}</SelectItem>
                    <SelectItem value="bags">{t('products.packaging.bags')}</SelectItem>
                    <SelectItem value="bulk">{t('products.packaging.bulk')}</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={variantSkuFilter} onValueChange={handleVariantSkuFilterChange}>
                  <SelectTrigger className="h-11 w-full sm:h-9 sm:w-[220px]">
                    <SelectValue placeholder={t('products.form.sku')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_SKUS_FILTER_VALUE}>
                      {t('products.filter.allSkus', 'All SKUs')}
                    </SelectItem>
                    {allSkus.map((sku) => (
                      <SelectItem key={sku.documentId} value={sku.documentId}>
                        {getLocalizedName(sku.name, locale)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isLoading && variantsForDisplay.length === 0 ? (
                <ProductsSkeleton />
              ) : variantsForDisplay.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  {t('products.noVariants')}
                </div>
              ) : viewMode === 'cards' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {variantsForDisplay.map((variant) => (
                    <VariantCard
                      key={variant.documentId}
                      variant={variant}
                      onSelect={handleEditVariant}
                      onEdit={handleEditVariant}
                      onDelete={handleDeleteVariant}
                      onPortalAvailabilityChange={handlePortalAvailabilityChange}
                      isUpdatingPortalAvailability={Boolean(
                        portalAvailabilityUpdatingById[variant.documentId]
                      )}
                      locale={locale}
                    />
                  ))}
                </div>
              ) : (
                <div className="glass-card max-w-full overflow-hidden rounded-2xl border border-border/50">
                  <div className="overflow-x-auto">
                    <Table className="min-w-[780px]">
                      <TableHeader>
                        <TableRow className="border-border/40 hover:bg-transparent bg-muted/50">
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.table.name')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.table.code')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.form.sku')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.table.packaging')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.table.weight')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('products.table.status')}</TableHead>
                          <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider text-end w-[180px]">{t('products.table.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {variantsForDisplay.map((variant) => (
                          <TableRow
                            key={variant.documentId}
                            className="cursor-pointer border-border/50 hover:bg-secondary/30 transition-colors"
                            onClick={() => handleEditVariant(variant)}
                          >
                            <TableCell className="px-5 py-4 font-medium">{getLocalizedName(variant.name, locale)}</TableCell>
                            <TableCell className="px-5 py-4">{variant.variantCode}</TableCell>
                            <TableCell className="px-5 py-4">{variant.sku ? getLocalizedName(variant.sku.name, locale) : '-'}</TableCell>
                            <TableCell className="px-5 py-4">{t(`products.packaging.${variant.packaging}`)}</TableCell>
                            <TableCell className="px-5 py-4">{formatWeight(variant.weight)}</TableCell>
                            <TableCell className="px-5 py-4">{variant.isActive ? t('common.active') : t('common.inactive')}</TableCell>
                            <TableCell className="px-5 py-4 text-end">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-9 w-9 rounded-xl hover:bg-muted"
                                    aria-label={t('common.actions')}
                                  >
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  onClick={(e) => e.stopPropagation()}
                                  onPointerDown={(e) => e.stopPropagation()}
                                >
                                  <DropdownMenuItem
                                    onSelect={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      handleEditVariant(variant)
                                    }}
                                  >
                                    {t('common.edit')}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      handleDeleteVariant(variant)
                                    }}
                                  >
                                    {t('common.delete')}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Category Form Drawer */}
      <Suspense fallback={null}>
        <CategoryFormDrawer
          isOpen={isCategoryFormOpen}
          onClose={() => setIsCategoryFormOpen(false)}
          category={selectedCategory}
          onSubmit={handleCategoryFormSubmit}
          isSaving={isSaving}
        />
      </Suspense>

      {/* SKU Form Drawer */}
      <Suspense fallback={null}>
        <SKUFormDrawer
          isOpen={isSkuFormOpen}
          onClose={() => setIsSkuFormOpen(false)}
          sku={selectedSku}
          categories={categories}
          onSubmit={handleSkuFormSubmit}
          isSaving={isSaving}
        />
      </Suspense>

      {/* Variant Form Drawer */}
      <Suspense fallback={null}>
        <VariantFormDrawer
          isOpen={isVariantFormOpen}
          onClose={() => setIsVariantFormOpen(false)}
          variant={selectedVariant}
          skus={skuSummaries}
          defaultSkuId={selectedSku?.documentId}
          onSubmit={handleVariantFormSubmit}
          isSaving={isSaving}
          pickImageFiles={pickImageFiles}
        />
      </Suspense>

      {/* SKU Detail Drawer */}
      <Suspense fallback={null}>
        <SKUDetailDrawer
          isOpen={isSkuDetailOpen}
          onClose={() => setIsSkuDetailOpen(false)}
          sku={selectedSku}
          variants={skuVariants}
          onEdit={handleEditSku}
          onDelete={handleDeleteSku}
          onAddVariant={handleAddVariant}
        />
      </Suspense>

      {/* Delete Confirmation Dialog */}
      <Suspense fallback={null}>
        <DeleteProductDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleConfirmDelete}
          item={deleteItemType === 'sku' ? selectedSku : selectedVariant}
          itemType={deleteItemType}
          isDeleting={isSaving}
        />
      </Suspense>
    </div>
  )
}
